import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import type { DocumentRepository } from '../../application/DocumentRepository'
import type { ArchivedDocument } from '../../domain/document'

const signedUrlTtlSeconds = 300

const documentRowSchema = z.object({
  id: z.uuid(),
  nom: z.string().min(1),
  type: z.string().min(1),
  document_version: z.number().int().positive().nullable(),
  content_type: z.string().nullable(),
  taille_octets: z.coerce.number().int().nonnegative().nullable(),
  sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
  storage_path: z.string().min(1),
  created_at: z.string(),
})

const linkRowSchema = z.object({
  document: documentRowSchema.nullable(),
})

function mapDocument(row: z.infer<typeof documentRowSchema>): ArchivedDocument {
  return {
    id: row.id,
    name: row.nom,
    kind: row.type,
    ...(row.document_version === null ? {} : { version: row.document_version }),
    ...(row.content_type === null ? {} : { contentType: row.content_type }),
    ...(row.taille_octets === null ? {} : { sizeBytes: row.taille_octets }),
    ...(row.sha256 === null ? {} : { sha256: row.sha256 }),
    storagePath: row.storage_path,
    createdAt: row.created_at,
  }
}

/**
 * Rattrapage chantier `audit-ui-v2` (item E) : logique d'upload générique —
 * extraite de l'ancien `uploadClientDocument` (seul appelant avant ce lot)
 * pour être partagée avec `uploadPaymentDocument`, plutôt que dupliquée.
 * Même comportement exact pour le Client : chemin, SHA-256, appel RPC.
 */
async function uploadDocument(
  storageClient: SupabaseClient,
  params: {
    bucket: string
    entityId: string
    file: File
    rpcName: string
    rpcIdField: string
    documentKind: string
  },
): Promise<ArchivedDocument> {
  const { bucket, entityId, file, rpcName, rpcIdField, documentKind } = params
  const safeName = file.name.replace(/[^\w.-]+/g, '_').slice(0, 200)
  const path = `${entityId}/${String(Date.now())}-${safeName}`
  const buffer = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  const sha256 = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  const upload = await storageClient.storage
    .from(bucket)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false })
  if (upload.error) throw new Error(`DOCUMENT_UPLOAD_FAILED:${upload.error.message}`)
  const rpcClient = storageClient as unknown as {
    rpc: (name: string, args: Record<string, unknown>) => Promise<unknown>
  }
  const response = (await rpcClient.rpc(rpcName, {
    p_command: {
      [rpcIdField]: entityId,
      storagePath: path,
      fileName: file.name,
      sizeBytes: file.size,
      contentType: file.type || 'application/octet-stream',
      sha256,
    },
  })) as { data?: unknown; error?: { message?: string } | null }
  if (response.error) throw new Error(response.error.message ?? 'DOCUMENT_ARCHIVE_FAILED')
  const archived = z.object({ documentId: z.uuid() }).safeParse(response.data)
  return {
    id: archived.success ? archived.data.documentId : crypto.randomUUID(),
    name: file.name,
    kind: documentKind,
    version: 1,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    sha256,
    storagePath: path,
    createdAt: new Date().toISOString(),
  }
}

export function createSupabaseDocumentRepository(client: AppSupabaseClient): DocumentRepository {
  const storageClient = client as unknown as SupabaseClient
  return {
    async listForEntity(query) {
      const response = (await storageClient
        .from('document_links')
        .select(
          'document:documents(id,nom,type,document_version,content_type,taille_octets,sha256,storage_path,created_at)',
        )
        .eq('entity_type', query.entityType)
        .eq('entity_id', query.entityId)) as {
        data: unknown
        error: { message: string; code?: string } | null
      }
      if (response.error) throw new Error(`DOCUMENT_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      const parsed = z.array(linkRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('DOCUMENT_LIST_RESPONSE_INVALID')
      const documents = parsed.data
        .flatMap((link) => (link.document ? [mapDocument(link.document)] : []))
        .sort((first, second) => second.createdAt.localeCompare(first.createdAt))
      return { documents, meta: { source: 'supabase', retrievedAt: new Date().toISOString() } }
    },
    async createDownloadUrl(storagePath, bucket) {
      const signed = await storageClient.storage
        .from(bucket)
        .createSignedUrl(storagePath, signedUrlTtlSeconds)
      if (signed.error) {
        reportUnexpectedError(signed.error, { feature: 'documents', operation: 'sign-url' })
        throw new Error('DOCUMENT_SIGNED_URL_FAILED')
      }
      return signed.data.signedUrl
    },

    async uploadClientDocument({ clientId, file }) {
      return uploadDocument(storageClient, {
        bucket: 'client-documents',
        entityId: clientId,
        file,
        rpcName: 'archive_client_document_v1',
        rpcIdField: 'clientId',
        documentKind: 'client_document',
      })
    },
    async uploadPaymentDocument({ paymentId, file }) {
      return uploadDocument(storageClient, {
        bucket: 'client-documents',
        entityId: paymentId,
        file,
        rpcName: 'archive_payment_document_v1',
        rpcIdField: 'paymentId',
        documentKind: 'payment_document',
      })
    },
  }
}
